import { addNotification } from "layouts/AppLayout/modules";
import { Dispatch, useCallback } from "react";
import { Action } from "redux";
import { CombinedError, OperationContext, OperationResult } from "urql";
import { reportStudioError } from "utils/clientErrorReporting";
import { useSoftMemo } from "utils/useSoftMemo";

/**
 * Cleans graphql errors for end user's
 *
 * @param errorMessage - Error message
 */
export function cleanErrorMessage(errorMessage: string): string {
    return errorMessage
        .replace(/\[GraphQL\] /g, "")
        .replace(/\[Network\] /g, "");
}

export async function exposeNetworkErrorMessage(result: OperationResult<any>): Promise<string> {
    if (result.error && !result.error.message.startsWith("[Network] ")) {
        return result.error.message;
    } else {
        try {
            const networkErrorMessage = await result.error.response.json();

            return networkErrorMessage.errors[0].message;
        } catch (e) {
            throw e;
        }
    }
}

export interface WrapUrqlFuncProps<T extends Action = Action> {
    /**
     * The dispatch to dispatch the notifications to.
     *
     * If not provided, then the global one provided by `react-redux's` "useDispatch"
     * will be used.
     *
     * @type {Dispatch<unknown>}
     * @memberof WrapUrqlFuncProps
     */
    dispatch: Dispatch<unknown>;
    /**
     * The message to display to the user on success or
     * false if there is to be no message.
     *
     * @type {(boolean | string)}
     * @memberof WrapUrqlFuncProps
     */
    onSuccessMessage?: boolean | string;
    /**
     * The message to display to the user on error or
     * false if there is to be no message.
     *
     * @type {(boolean | string)}
     * @memberof WrapUrqlFuncProps
     */
    onErrorMessage?: boolean | string;

    /**
     * Actions that should be dispatched to redux on successful operation.
     *
     * @type {Action[]}
     * @memberof WrapUrqlFuncProps
     */
    dispatchOnSuccess?: Action[];

    /**
     * Actions that should be dispatched to redux on errors.
     *
     * @type {Action[]}
     * @memberof WrapUrqlFuncProps
     */
    dispatchOnError?: Action[];
}

export type UrqlFunc<V, Result> = (variables?: V, context?: Partial<OperationContext>) => Promise<OperationResult<Result>>;

/**
 * Takes an Urql function and wraps it for common functionality.
 *
 * By default, any errors caught will be displayed as a notification to the user.
 * A "Success" message will be displayed to the user upon successes.
 *
 * The response of the urql will be passed to the `then` statement.
 *
 * To do custom handling, then set both items to false and handle the response in the `then`.
 *
 * @export
 * @template Result
 * @param {(...args: any[]) => Promise<Result>} func
 */
export function wrapUrqlFunc<V, Result>(func: UrqlFunc<V, Result>, props: WrapUrqlFuncProps = { dispatch: undefined }): (variables?: V, context?: Partial<OperationContext>) => Promise<OperationResult<Result>> {
    return (...args: any[]) => {
        return Promise.resolve() // Wrap the function call in a Promise chain.
            .then(() => func(...args))
            .catch((e) => {
                // Something when horribly wrong here.
                reportStudioError(e, "error");
                return {
                    error: e,
                    operation: {
                        context: undefined,
                        kind: undefined,
                    },
                } as OperationResult<Result>;
            })
            .then(async response => {
                if (response.error) {
                    await handleError(response.error, props);
                } else if (props.onSuccessMessage !== false) {
                    handleSuccess(response, props);
                }
                return response;
            });
    };
}

// another function to minimize impact
export function useWrapUrqlFunc<V, Result>(func: UrqlFunc<V, Result>, props: WrapUrqlFuncProps = { dispatch: undefined }): (variables?: V, context?: Partial<OperationContext>) => Promise<OperationResult<Result>> {

    const propsMemo = useSoftMemo<WrapUrqlFuncProps>(props);

    return useCallback((...args: any[]) => {
        return Promise.resolve() // Wrap the function call in a Promise chain.
            .then(() => func(...args))
            .catch((e) => {
                // Something when horribly wrong here.
                reportStudioError(e, "error");
                return {
                    error: e,
                    operation: {
                        context: undefined,
                        kind: undefined,
                    },
                } as OperationResult<Result>;
            })
            .then(async response => {
                if (response.error) {
                    await handleError(response.error, propsMemo);
                } else if (propsMemo.onSuccessMessage !== false) {
                    handleSuccess(response, propsMemo);
                }
                return response;
            });
    }, [func, propsMemo]);
}

async function handleError(error: CombinedError, props: WrapUrqlFuncProps) {

    const { dispatch, onErrorMessage, dispatchOnError = [] } = props;

    if (dispatch && onErrorMessage !== false) {
        // We are going to try to get an error message in a couple of ways
        // 1. In the onErrorMessage
        const message: string = typeof onErrorMessage === "string" ?
            onErrorMessage :
            cleanErrorMessage(error.message);
        // If the message has any length to it add it.
        if (message.trim().length > 0) {
            dispatchOnError.unshift(addNotification(message, true));
        }

        // 2. Other thing to try is the actual response
        if (error.response) {
            const response = error.response;
            // You can only call response.json() once
            // otherwise it throws an error
            try {
                // Wrapped in a try catch because response.json() can only be called once
                // and it may have been called already
                const json = await response.json();
                if (Array.isArray(json.errors) && json.errors.length > 0) {
                    json.errors.forEach((bodyError: any) => {
                        dispatchOnError.unshift(addNotification(bodyError.message, true));
                    });
                }
            } catch (e: any) {
                // Error occurred
                // Swallow the ones about stream already ready, they become a red herring
                // if an error happened in the execution
                if (!e.message.startsWith("Failed to execute 'json'")) {
                    console.error(e);
                }
            }
        }
    }
    for (const itemToDispatch of dispatchOnError) {
        dispatch(itemToDispatch);
    }
}

function handleSuccess(response: OperationResult<unknown>, props: WrapUrqlFuncProps) {
    const { dispatch, onSuccessMessage, dispatchOnSuccess = [] } = props;
    if (dispatch && onSuccessMessage !== false) {
        const message: string = typeof onSuccessMessage === "string" ?
            onSuccessMessage :
            "Success";
        dispatchOnSuccess.unshift(addNotification(message, false));
    }
    for (const itemToDispatch of dispatchOnSuccess) {
        dispatch(itemToDispatch);
    }
}